import {
  Paper,
  Box,
  FormControl,
  InputLabel,
  TextField,
  Select,
  MenuItem,
  Button,
  Typography,
  SelectChangeEvent,
} from "@mui/material";
import DashboardFrame from "../../component/dashboard-frame";
import React, { useEffect, useState } from "react";
import { CampaignStatus } from "../../enum/campaign";
import Table from "../../component/table";
import { IWithClassName } from "../../theme/default.theme";
import { CalendarMonth, Search, KeyboardArrowDown, Download } from "@mui/icons-material";
import StyledMenu from "./filter-menu";
import { useAppSelector, useAppDispatch, IRootState } from "../../redux/store";
import { ITableColumn } from "../../component/table/component";
import { fetchReportThunk } from "../../redux/thunks/report.thunk";
import { getAppsThunk } from "../../redux/thunks/app.thunk";
import { getCampaignThunk } from "../../redux/thunks/campaign.thunk";
import { createSelector } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { RangeKeyDict, Range } from "react-date-range";
import { ReportGroup } from "../../enum/report";
import DatePicker from "../../component/date-picker";

interface IProps extends IWithClassName {}

const columns = [
  {
    id: "app",
    title: "Apps / Web URL",
    sortable: false,
    isNumber: false,
  },
  {
    id: "campaignId",
    title: "Campaign Id",
    sortable: true,
    isNumber: false,
  },
  {
    id: "campaignName",
    title: "Campaign Name",
    sortable: true,
    isNumber: false,
  },
  // {
  //   id: "impressions",
  //   title: "Impressions",
  //   sortable: true,
  //   isNumber: true,
  // },
  {
    id: "clicks",
    title: "Clicks",
    sortable: true,
    isNumber: true,
  },
  // {
  //   id: "ctr",
  //   title: "CTR",
  //   sortable: true,
  //   isNumber: true,
  // },
  {
    id: "installs",
    title: "Installs",
    sortable: true,
    isNumber: true,
  },
  {
    id: "conversion",
    title: "Conversion",
    sortable: true,
    isNumber: true,
  },
  {
    id: "cr",
    title: "CR",
    sortable: true,
    isNumber: true,
  },
  {
    id: "cvr",
    title: "CVR",
    sortable: true,
    isNumber: true,
  },
  {
    id: "spend",
    title: "Spends",
    sortable: true,
    isNumber: true,
  },
  // {
  //   id: "cpc",
  //   title: "CPC",
  //   sortable: true,
  //   isNumber: true,
  // },
  {
    id: "cpi",
    title: "eCPI",
    sortable: true,
    isNumber: true,
  },
  // {
  //   id: "cpa",
  //   title: "CPA",
  //   sortable: true,
  //   isNumber: true,
  // },
];

const initFilter = {
  startDate: dayjs().format("YYYY-MM-DD"),
  endDate: dayjs().format("YYYY-MM-DD"),
  includeImpression: true,
  kpiFields: "",
  groupBy: "",
  app: "",
  campaign: "",
  status: "",
  keyword: "",
  page: 1,
  column: "",
  order: "",
};

const selector = createSelector(({ app }: IRootState) => app,
({ campaign }: IRootState) => campaign,
({ report }: IRootState) => report, 
(app, campaign, report) => ({
  apps: {
    data: app.apps.data ?? [],
    isLoading: app.apps.isLoading,
  },
  campaigns: {
    data: campaign.list.data ?? [],
    isLoading: campaign.list.isLoading,
  },
  report: {
    data: report.report.data ?? [],
    isLoading: report.report.isLoading,
  },
}));

const Report = ({ className }: IProps) => {
  const dispatch = useAppDispatch();

  const { apps, campaigns, report } = useAppSelector(selector);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openDatePicker, setDatePicker] = useState<null | HTMLElement>(null);
  const [filters, setFilters] = useState(initFilter);
  const [optionalColumns, setOptionalColumns] = useState<string>("");
  const [displayColumns, setDisplayColumns] = useState<Array<{
    id: string,
    title: string,
    sortable: boolean,
  }>>([]);
  const [datePickerRange, setDatePickerRange] = useState<Array<Range>>(
    [{
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection'
    }]);

  useEffect(() => {
    dispatch(fetchReportThunk(initFilter));

    dispatch(getAppsThunk());

    dispatch(getCampaignThunk());
  }, [dispatch]);

  useEffect(() => {
    const optionalColumnArray = optionalColumns.split(",").map((column) => {
      switch(column) {
        case ReportGroup.GoalName:
          return({
            id: "campaignGoal",
            title: "Goal Name",
            sortable: true,
          });
        case ReportGroup.Country:
          return({
            id: "country",
            title: "Country",
            sortable: true,
          });
        case ReportGroup.City:
          return({
            id: "city",
            title: "City",
            sortable: true,
          });
        case ReportGroup.OS:
          return({
            id: "os",
            title: "OS",
            sortable: true,
          });
        case ReportGroup.Month:
          return({
            id: "month",
            title: "Month",
            sortable: true,
          });
        case ReportGroup.Hour:
          return({
            id: "hour",
            title: "Hour",
            sortable: true,
          });
        case ReportGroup.Date:
          return({
            id: "created",
            title: "Date",
            sortable: true,
          });
        case ReportGroup.Status:
          return({
            id: "campaignStatus",
            title: "Status",
            sortable: true,
          });
        default:
          return({
            id: "campaignStatus",
            title: "Status",
            sortable: true,
          });
      }
    });

    setDisplayColumns([...columns, ...optionalColumnArray]);
  }, [optionalColumns])

  const groupByHandler = (value: ReportGroup, checked: boolean) => {
    if(checked) {
      setFilters((filters) => {
        const values = filters.groupBy.length ? filters.groupBy.split(",") : [];
        values.push(value);

        return({
        ...filters,
        groupBy: values.join(","),
      })});
    } else {
      setFilters((filters) => ({
        ...filters,
        groupBy: filters.groupBy.split(",").filter((val) => val !== value).join(","),
      }));
    }
    
  }

  const onPageChange = (page: number) => {
    dispatch(fetchReportThunk({ ...filters, page }));
    
    setFilters((filters) => ({
      ...filters,
      page,
    }));
  }

  const onColumnOrder = (columnIndex: number, column: ITableColumn, order: "asc" | "desc") => {
    dispatch(fetchReportThunk({ ...filters, column: column.id, order }));

    setFilters((filters) => ({
      ...filters,
      column: column.id,
      order,
    }));
  }

  const renderCell = (rowIndex: number, columnIndex: number, column: ITableColumn) => {
    if(report.data) {
      const rowData = report.data[rowIndex];
      const cellValue = rowData[column.id as keyof typeof rowData];

      switch (column.id) {
        case "cpi":
          return rowData.installs === 0 ? "$0" : "$" + (rowData.spend/rowData.installs).toFixed(2);
        case "cr":
          return cellValue + "%";
        case "cvr":
          return cellValue + "%";
        case "spend":
          return cellValue.toString().length ? "$" + cellValue?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "$0";
        default:
          return column.id === "app" ? <Box>{rowData.thumbnail ? <img src={rowData.thumbnail} alt="app"/> : <a href={rowData.webUrl} rel="noreferrer" target="_blank">{rowData.webUrl}</a>}</Box> : <Box>{column.isNumber ? cellValue?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : cellValue}</Box>;
      }
    } else {
      return <Box>Loading...</Box>;
    }
  }

  const dateHandler = ({ selection }: RangeKeyDict) => {
    setDatePickerRange([selection]);
    // const selectedPeriod = reportPeriodOptions.find(
    //   ({value}) => value === target.value
    // );

    if (datePickerRange) {
      setFilters({
        ...filters,
        startDate: dayjs(selection.startDate).format("YYYY-MM-DD"),
        endDate: dayjs(selection.endDate).format("YYYY-MM-DD"),
      });
    }
  }

  const changeHandler = (label: string) =>
    (event: SelectChangeEvent<string>) => {
      setFilters({
        ...filters,
        [label]: event.target.value as string,
      });
  };

  const searchReport = () => {
    setAnchorEl(null);
    setOptionalColumns(filters.groupBy);
    dispatch(fetchReportThunk(filters));
  }

  const downloadFile = () => {
    const header = displayColumns.map((column) => column.title).join(",");
    const rowsData = header + "\n" + report.data.map((row) => displayColumns.map((column) => row[column.id as keyof typeof row]).join(",")).join("\n");
    const csvContent = `data:text/csv;charset=utf-8,${rowsData}`;
    const encodedURI = encodeURI(csvContent);
    window.open(encodedURI);
  }

  return (
    <DashboardFrame title="Reporting">
      <Box className={className}>
        <Box component={Paper}>
          <Box className="report-filter">
            <FormControl className="report-period-cont">
              <TextField 
                onClick={({ currentTarget }) => setDatePicker(currentTarget)}
                InputProps={{endAdornment: <CalendarMonth />}}
                value={`${filters.startDate} - ${filters.endDate}`} />
              <DatePicker openDatePicker={openDatePicker} dateHandler={dateHandler} datePickerRange={datePickerRange} setDatePicker={setDatePicker} />
            </FormControl>
            <FormControl>
              <InputLabel>App</InputLabel>
              <Select className="app" onChange={changeHandler("app")} value={filters.app}>
                {apps.isLoading ? (
                  <MenuItem>Loading...</MenuItem>
                ) : (
                  apps.data.map((app) => (
                    <MenuItem value={app.id} key={app.id}>
                      {app.title} [{app.type}]
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel>Campaign</InputLabel>
              <Select className="campaign" onChange={changeHandler("campaign")} value={filters.campaign}>
                {campaigns.isLoading ? (
                  <MenuItem>Loading...</MenuItem>
                ) : (
                  campaigns.data.map((campaign) => (
                    <MenuItem value={campaign.id} key={campaign.id}>
                      {campaign.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                placeholder="Status"
                className="campaign-status"
                onChange={changeHandler("status")}
                value={filters.status}
              >
                {Object.keys(CampaignStatus).map((status) => {
                  const statusValue =
                    CampaignStatus[status as keyof typeof CampaignStatus];
                  return (
                    <MenuItem
                      value={statusValue}
                      key={statusValue}
                      sx={{ textTransform: "capitalize" }}
                    >
                      {statusValue}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <Button variant="contained" className="search-button" onClick={searchReport}>
              Search
            </Button>
          </Box>
        </Box>

        <Box component={Paper} className="table-container">
          <Box className="table-view-header">
            <Box className="table-view-heading">
              <Typography variant="h6">Overview</Typography>
            </Box>
            <Box className="table-view-filter-cont">
              <TextField
                onChange={({target}) => setFilters((filters) => ({...filters, keyword: target.value}))}
                InputProps={{
                  endAdornment: <Button variant="contained" onClick={searchReport}><Search /></Button>,
                }}
              />

              <FormControl>
                <TextField
                  placeholder="Group By"
                  label="Group By"
                  onClick={({ currentTarget }) => setAnchorEl(currentTarget)}
                  value={filters.groupBy}
                  InputLabelProps={{ shrink: !!filters.groupBy.length }}
                  InputProps={{
                    endAdornment: <KeyboardArrowDown />,
                  }}
                />
                <StyledMenu 
                  anchorEl={anchorEl} 
                  setAnchorEl={setAnchorEl} 
                  groupByHandler={groupByHandler}
                  searchReport={searchReport}
                  groupBy={filters.groupBy} />
              </FormControl>

              <FormControl>
                <Button variant="contained" className="download-button" onClick={downloadFile}><Download /></Button>
              </FormControl>
            </Box>
          </Box>

          <Table
            columns={displayColumns}
            onPageChange={onPageChange}
            rowsPerPage={10}
            totalRows={report.data.length}
            currentPage={filters.page}
            renderCell={renderCell}
            uniqueRowId={(id) => id.toString()}
            onColumnOrder={onColumnOrder}
          />
        </Box>
      </Box>
    </DashboardFrame>
  );
};

export default Report;
